import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Aurora } from "@/components/motion/Aurora";
import RevealTitle from "@/components/motion/RevealTitle";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentResult({ kind = "deposit" }) {
  const { result } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const code = params.get("code");
  const [status, setStatus] = useState("checking");
  const [holdMode, setHoldMode] = useState(false);
  const [signalInfo, setSignalInfo] = useState(null);
  const [extrasInfo, setExtrasInfo] = useState(null); // {amount, paid}
  const [error, setError] = useState("");

  useEffect(() => {
    if (result === "cancelado") {
      setStatus("cancelled");
      return;
    }
    if (!sessionId) {
      setStatus("error");
      setError("Sesión de pago no encontrada");
      return;
    }
    let attempts = 0;
    const endpoint = kind === "extras"
      ? `/payments/extras/status/${sessionId}`
      : kind === "signal"
      ? `/payments/signal/status/${sessionId}`
      : kind === "rental"
      ? `/payments/rental/status/${sessionId}`
      : `/payments/checkout/status/${sessionId}`;
    const poll = async () => {
      try {
        const { data } = await api.get(endpoint);
        if (kind === "extras") {
          if (data.extras_payment_status === "paid") {
            setStatus("success");
            return;
          }
        } else if (kind === "rental") {
          if (data.rental_payment_status === "paid") {
            setStatus("success");
            return;
          }
        } else if (kind === "signal") {
          if (data.signal_payment_status === "paid") {
            setSignalInfo({ balance: Number(data.balance_amount || 0), deposit: Number(data.deposit_amount || 0) });
            setStatus("success");
            return;
          }
        } else if (data.reservation_payment_status === "paid" || data.reservation_payment_status === "on_hold") {
          setHoldMode(data.reservation_payment_status === "on_hold");
          if (Number(data.extras_amount || 0) > 0) {
            setExtrasInfo({
              amount: Number(data.extras_amount),
              paid: data.extras_payment_status === "paid",
            });
          }
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        setError(formatError(err));
        setStatus("error");
      }
    };
    poll();
  }, [result, sessionId, kind]);

  return (
    <div className="aurora-page relative isolate min-h-screen bg-forest-deep flex items-center justify-center p-6">
      <Aurora variant="dark" fixed />
      <div className="bg-white border border-sand rounded-2xl p-8 max-w-md w-full text-center fade-in">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto mb-3 text-forest-deep animate-spin" />
            <RevealTitle as="h2" immediate className="font-display text-xl font-bold text-charcoal">
              Procesando pago...
            </RevealTitle>
            <p className="text-sm text-warmgray mt-1">
              Verificando con Stripe, no cierres esta ventana.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-14 w-14 rounded-full bg-ok text-white flex items-center justify-center mx-auto mb-4">
              <Check className="h-7 w-7" />
            </div>
            <RevealTitle as="h2" immediate className="font-display text-2xl font-bold text-charcoal mb-2">
              {kind === "extras" ? "¡Extras pagados!" : kind === "rental" ? "¡Alquiler pagado!" : kind === "signal" ? "¡Señal recibida!" : holdMode ? "¡Fianza preautorizada!" : "¡Pago recibido!"}
            </RevealTitle>
            <p className="text-warmgray mb-4">
              {kind === "extras"
                ? "Hemos cobrado los extras de tu reserva. ¡Todo listo para la entrega!"
                : kind === "signal"
                ? `Tu reserva queda confirmada. Unos días antes de la entrega te enviaremos un correo para pagar el resto del alquiler${signalInfo?.balance ? ` (${signalInfo.balance.toFixed(2)} €)` : ""} y dejar la fianza${signalInfo?.deposit ? ` (${signalInfo.deposit.toFixed(0)} €)` : ""} retenida en tu tarjeta.`
                : kind === "rental"
                ? "Hemos recibido el pago del alquiler de tu reserva. ¡Buen viaje!"
                : holdMode
                ? "Tu tarjeta queda retenida sin cobro real. Liberaremos la fianza tras el check-out sin incidencias."
                : "Tu fianza está retenida correctamente. ¡Disfruta del viaje!"}
            </p>
            {kind !== "extras" && kind !== "rental" && kind !== "signal" && extrasInfo && (
              <div
                className={`rounded-xl p-3 mb-6 text-sm ${
                  extrasInfo.paid
                    ? "bg-ok/10 border border-ok/30 text-charcoal"
                    : "bg-warn/10 border border-warn/30 text-warn-ink"
                }`}
                data-testid="payresult-extras"
              >
                {extrasInfo.paid
                  ? `Extras cobrados: ${extrasInfo.amount.toFixed(2)} € ✓`
                  : `Extras pendientes (${extrasInfo.amount.toFixed(2)} €): revisa tu email o tu reserva para completar el pago.`}
              </div>
            )}
            <Button
              onClick={() => navigate(`/reserva/${code}`)}
              className="bg-forest-deep hover:bg-forest text-white"
              data-testid="payresult-back-btn"
            >
              Volver a mi reserva
            </Button>
          </>
        )}
        {(status === "cancelled" || status === "expired" || status === "timeout" || status === "error") && (
          <>
            <div className="h-14 w-14 rounded-full bg-danger text-white flex items-center justify-center mx-auto mb-4">
              <X className="h-7 w-7" />
            </div>
            <RevealTitle as="h2" immediate className="font-display text-2xl font-bold text-charcoal mb-2">
              {status === "cancelled" ? "Pago cancelado" :
               status === "expired" ? "Sesión expirada" :
               status === "timeout" ? "Verificación pendiente" :
               "Error en el pago"}
            </RevealTitle>
            <p className="text-warmgray mb-6">
              {error || "Puedes intentar el pago nuevamente desde tu reserva."}
            </p>
            <Button
              onClick={() => navigate(`/checkin/${code}`)}
              className="bg-forest-deep hover:bg-forest text-white"
              data-testid="payresult-retry-btn"
            >
              Reintentar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
